<template>
  <div class="border border-brand_gray1 p-4 col-span-2 rounded-md">
    <!-- Checkbox Area -->
    <div class="flex-col mb-2">

      <p class="text-brand_gray3 font-semibold mb-3">
        Mode Of Payment :
        <span v-if="selectedInvoice" class="text-brand_orange3">
          {{ selectedInvoice?.mode_of_payment?.toUpperCase() }}
        </span>
      </p>

      <div v-if="!selectedInvoice" class="flex mt-2 gap-5 text-brand_gray3">

        <label class="flex gap-2 center-y cursor-pointer">
          <RadioButton v-model="formData.modeOfPayment" id="default-radio-1" value="cash"
            class="w-5 h-5 cursor-pointer" />
          Cash
        </label>

        <label class="flex gap-2 center-y cursor-pointer">
          <RadioButton v-model="formData.modeOfPayment" checked id="default-radio-2" value="check"
            class="w-5 h-5 cursor-pointer" />
          Check
        </label>

      </div>

    </div>

    <!-- FORM GROUP 1 -->
    <div v-if="
      selectedInvoice?.mode_of_payment === 'check' ||
      formData.modeOfPayment === 'check'
    " class="flex center-all w-full">
      <!-- Check No. -->
      <div class="flex-col w-full p-1">
        <label for="checkNo" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          Check No. :
        </label>

        <!-- Readonly (when selectedInvoice exists) -->
        <InputText v-if="selectedInvoice" :modelValue="selectedInvoice.Check_no" inputId="checkNo" readonly
          class="block w-11/12 bg-[#FAFAFA] border border-[#DBDBDB] text-gray-700 rounded-md py-2 px-3 leading-tight focus:outline focus:bg-white" />

        <!-- Editable -->
        <InputText v-else v-model="formData.checkNo" inputId="checkNo" placeholder="Check No."
          class="block w-11/12 bg-[#FAFAFA] border border-[#DBDBDB] text-gray-700 rounded-md py-2 px-3 leading-tight focus:outline focus:bg-white" />
      </div>

      <!-- Bank -->
      <div class="flex-col w-full p-1">
        <label for="bank" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          Bank :
        </label>

        <!-- Readonly (when selectedInvoice exists) -->
        <InputText v-if="selectedInvoice" :modelValue="selectedInvoice.Bank" inputId="bank" readonly
          class="block w-11/12 bg-[#FAFAFA] border border-[#DBDBDB] text-gray-700 rounded-md py-2 px-3 leading-tight focus:outline focus:bg-white" />

        <!-- Editable -->
        <InputText v-else v-model="formData.bank" inputId="bank" placeholder="Bank"
          class="block w-11/12 bg-[#FAFAFA] border border-[#DBDBDB] text-gray-700 rounded-md py-2 px-3 leading-tight focus:outline focus:bg-white" />
      </div>
    </div>

    <hr class="my-2" />
    <div class="text text-brand_gray3 p-1 flex items-center gap-2 my-1">
      <label class="flex items-center gap-2 cursor-pointer">
        <span class="font-semibold">With Withholding :</span>

        <!-- Show selected invoice (readonly) -->
        <Checkbox v-if="selectedInvoice" :modelValue="selectedInvoice.payee?.with_withholding_tax" binary disabled />

        <!-- Editable for new invoice -->
        <Checkbox v-else v-model="formData.withWithholding" @change="computeTransaction" binary />

        <!-- Yes / No text -->
        <span class="ml-2 font-medium" :class="(
          selectedInvoice
            ? selectedInvoice.payee?.with_withholding_tax
            : formData.withWithholding
        )
          ? 'text-green-600'
          : 'text-red-600'
          ">
          {{
            (
              selectedInvoice
                ? selectedInvoice.payee?.with_withholding_tax
                : formData.withWithholding
            )
              ? "Yes"
              : "No"
          }}
        </span>
      </label>
    </div>

    <!-- FORM GROUP 2 -->
    <div class="flex center-all w-full mb-1">
      <!-- VATable Sales -->
      <div class="flex flex-col w-full p-1">
        <label for="vatableSales" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          VATable Sales :
        </label>

        <!-- Readonly if selectedInvoice exists -->
        <InputNumber v-if="selectedInvoice" :modelValue="selectedInvoice.VATable_sales" inputId="vatableSales"
          :minFractionDigits="2" readonly fluid class="w-11/12" />

        <!-- Editable -->
        <InputNumber v-else @value-change="computeTransaction" v-model="formData.vatableSalesRaw" inputId="vatableSales"
          :minFractionDigits="2" fluid class="w-11/12" placeholder="VATable Sales" />

        <!-- Error Message -->
        <span v-if="errors && errors.vatableSalesRaw" class="text-red-500 text-sm mt-1">
          {{ errors.vatableSalesRaw }}
        </span>
      </div>

      <!-- Zero Rated Sales -->
      <div class="flex flex-col w-full p-1">
        <label for="zeroRatedSales" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          Zero Rated Sales :
        </label>

        <!-- Readonly if selectedInvoice exists -->
        <InputNumber v-if="selectedInvoice" :modelValue="selectedInvoice.zero_rated_sales" inputId="zeroRatedSales"
          :minFractionDigits="2" readonly fluid class="w-11/12" />

        <!-- Editable -->
        <InputNumber v-else @value-change="computeTransaction" v-model="formData.zeroRatedSales"
          inputId="zeroRatedSales" :minFractionDigits="2" fluid class="w-11/12" placeholder="Zero Rated Sales" />

        <!-- Error Message -->
        <span v-if="errors && errors.zeroRatedSales" class="text-red-500 text-sm mt-1">
          {{ errors.zeroRatedSales }}
        </span>
      </div>
    </div>

    <!-- FORM GROUP 3 -->
    <div class="flex center-all w-full mb-1">
      <!-- Vat-Exempt Sales -->
      <div class="flex flex-col w-full p-1">
        <label for="vatExemptSales" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          Vat-Exempt Sales :
        </label>

        <!-- Readonly (when selectedInvoice exists) -->
        <InputNumber v-if="selectedInvoice" :modelValue="selectedInvoice.vat_Exempt_sales" inputId="vatExemptSales"
          :minFractionDigits="2" readonly fluid class="w-11/12" />

        <!-- Editable -->
        <InputNumber v-else @value-change="computeTransaction" v-model="formData.vatExemptSales"
          inputId="vatExemptSales" :minFractionDigits="2" fluid class="w-11/12" placeholder="Vat-Exempt Sales" />

        <!-- Error Message -->
        <span v-if="errors && errors.vatExemptSales" class="text-red-500 text-sm mt-1">
          {{ errors.vatExemptSales }}
        </span>
      </div>

      <!-- VAT Amount -->
      <div class="flex-col w-full p-1">
        <label for="vatAmount" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          VAT Amount :
        </label>

        <!-- Readonly (when selectedInvoice exists) -->
        <InputNumber v-if="selectedInvoice" :modelValue="selectedInvoice.vat_amount" inputId="vatAmount"
          :minFractionDigits="2" readonly fluid class="w-11/12" />

        <!-- Disabled when no selectedInvoice -->
        <InputNumber v-else v-model="computedResult.vatAmount" inputId="vatAmount" :minFractionDigits="2" fluid disabled
          class="w-11/12" placeholder="VAT Amount" />
      </div>
    </div>

    <!-- FORM GROUP 4 -->
    <div class="flex w-full mb-1">
      <!-- Amount -->
      <div class="flex-col w-1/2 p-1">
        <label for="amount" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          <p class="font-semibold">Amount :</p>
        </label>

        <!-- Readonly if selectedInvoice exists -->
        <InputNumber v-if="selectedInvoice" :modelValue="selectedInvoice.amount" inputId="amount" :minFractionDigits="2"
          readonly fluid class="w-11/12" />

        <!-- Disabled if no selectedInvoice -->
        <InputNumber v-else v-model="computedResult.amount" inputId="amount" :minFractionDigits="2" fluid disabled
          class="w-11/12" placeholder="Amount" />
      </div>
    </div>

    <hr class="my-2" />

    <!-- FORM GROUP 5 -->
    <div class="flex center-all w-full mb-1">
      <!-- Total Sales (VAT Inclusive) -->
      <div class="flex-col w-full p-1">
        <label for="totalSales" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          Total Sales (VAT Inclusive) :
        </label>

        <InputNumber v-if="selectedInvoice" :modelValue="selectedInvoice.total_sales_vat_inclusive" inputId="totalSales"
          :minFractionDigits="2" readonly fluid class="w-11/12" />

        <InputNumber v-else v-model="computedResult.amount" inputId="totalSales" :minFractionDigits="2" fluid disabled
          class="w-11/12" placeholder="Total Sales (VAT Inclusive)" />
      </div>

      <!-- Less : SC/PWD Discount -->
      <div class="flex-col w-full p-1">
        <label for="scPwdDiscount" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          Less : SC/PWD Discount :
        </label>

        <InputNumber v-if="selectedInvoice" :modelValue="selectedInvoice.less_sc_pwd_discount" inputId="scPwdDiscount"
          :minFractionDigits="2" readonly fluid class="w-11/12" />

        <InputNumber v-else v-model="computedResult.lessScPwdDiscount" inputId="scPwdDiscount" :minFractionDigits="2"
          fluid disabled class="w-11/12" placeholder="Less : SC/PWD Discount" />
      </div>
    </div>

    <!-- FORM GROUP 6 -->
    <div class="flex center-all w-full mb-1">
      <!-- Less : VAT -->
      <div class="flex-col w-full p-1">
        <label for="lessVat" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          Less : VAT :
        </label>

        <InputNumber v-if="selectedInvoice" :modelValue="selectedInvoice.less_vat" inputId="lessVat"
          :minFractionDigits="2" readonly fluid class="w-11/12" />

        <InputNumber v-else v-model="computedResult.lessVat" inputId="lessVat" :minFractionDigits="2" fluid disabled
          class="w-11/12" placeholder="Less : VAT" />
      </div>

      <!-- Less : Withholding Tax -->
      <div class="flex-col w-full p-1">
        <label for="withholdingTax" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          Less : Withholding Tax :
        </label>

        <InputNumber v-if="selectedInvoice" :modelValue="selectedInvoice.less_withholding_tax" inputId="withholdingTax"
          :minFractionDigits="2" readonly fluid class="w-11/12" />

        <InputNumber v-else v-model="computedResult.lessWithholding" inputId="withholdingTax" :minFractionDigits="2"
          fluid disabled class="w-11/12" placeholder="Less : Withholding Tax" />
      </div>
    </div>

    <!-- FORM GROUP 7 -->
    <div class="flex center-all w-full mb-3">
      <!-- Amount : Net Of Vat -->
      <div class="flex-col w-full p-1">
        <label for="amountNetOfVat" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          Amount : Net Of Vat :
        </label>

        <InputNumber v-if="selectedInvoice" :modelValue="selectedInvoice.amount_net_of_vat" inputId="amountNetOfVat"
          :minFractionDigits="2" readonly fluid class="w-11/12" />

        <InputNumber v-else v-model="computedResult.amountNetOfVat" inputId="amountNetOfVat" :minFractionDigits="2"
          fluid disabled class="w-11/12" placeholder="Amount : Net Of Vat" />
      </div>

      <!-- Total Amount Due -->
      <div class="flex-col w-full p-1">
        <label for="totalAmountDue" class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          <p class="font-semibold">Total Amount Due :</p>
        </label>

        <InputNumber v-if="selectedInvoice" :modelValue="selectedInvoice.total_amount_due" inputId="totalAmountDue"
          :minFractionDigits="2" readonly fluid class="w-11/12" />

        <InputNumber v-else v-model="computedResult.totalAmountDue" inputId="totalAmountDue" :minFractionDigits="2"
          fluid disabled class="w-11/12" placeholder="Total Amount Due" />
      </div>
    </div>

    <hr class="my-2" />

    <!-- SIGNATURE FILE UPLOAD -->
    <!-- <div class="flex w-full mb-3">
      <div class="flex p-1 center-all gap-3">
        <label class="w-full text-brand_gray3 block tracking-wide text-base mb-2">
          <p class="font-semibold">Signature :</p>
        </label>
        <input
          class="bg-[#FAFAFA] border border-[#DBDBDB] text-gray-700 rounded-md py-2 px-3 leading-tight focus:outline focus:bg-white"
          type="file"
        />
      </div>
    </div> -->
  </div>
</template>

<script>
import { reactive, watch, inject, ref } from "vue";

export default {
  props: {
    selectedInvoice: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
    },
    prefillData: {
      type: Object,
    },
  },

  setup(props, { emit }) {
    const axios = inject("$axios");
    const formData = reactive({
      modeOfPayment: "Cash",
      checkNo: "",
      bank: "",
      withWithholding: false,
      vatableSalesRaw: 0,
      zeroRatedSales: 0,
      vatExemptSales: 0,
    });
    const computedResult = ref({});

    // Emit all changes upward
    watch(
      formData,
      (newVal) => {
        emit("totalSummary", { ...newVal });
      },
      { deep: true }
    );

    // Apply prefill data when parent sends it
    watch(
      () => props.prefillData,
      (newVal) => {
        if (newVal) Object.assign(formData, newVal);
      },
      { deep: true, immediate: true }
    );

    const computeTransaction = async () => {
      if (
        !formData.vatableSalesRaw &&
        !formData.zeroRatedSales &&
        !formData.vatExemptSales
      ) {
        computedResult.value = {};
        return;
      }

      try {
        const res = await axios.get(
          "/api/invoice/get/transaction/computation",
          {
            params: {
              withWithholding: formData.withWithholding,
              vatableSalesRaw: formData.vatableSalesRaw,
              zeroRatedSales: formData.zeroRatedSales,
              vatExemptSales: formData.vatExemptSales,
            },
          }
        );
        computedResult.value = res.data.data || {};
        console.log("Computed Result:", computedResult.value);
      } catch (error) {
        console.error("Error computing transaction:", error);
      }
    };

    return { formData, computedResult, computeTransaction };
  },
};
</script>
